import { action, observable, runInAction, toJS } from 'mobx';
import { API } from 'src/Services/Api/Api';
import { IReleaseNotes, IUser } from '../../Interfaces/UserInterface';
import Auth from '../../Services/Auth';
import { RootStore } from '../Root/RootStore';


const auth = new Auth();

class UserAPI {

    private api: API;

    constructor(Api: API) {
        this.api = Api;
    }

    public async getProfile() {
        try {
            const data = await this.api.get('/account');
            return data;
        }
        catch (error) {
            throw error;
        }
    }

    public async putProfile(profile: IUser) {
        try {
            const data = await this.api.put('/account', profile);
            return data;
        }
        catch (error) {
            throw error;
        }
    }

    public resetPassphrase() {
        return this.api.post('/account/resetpassphrase', {});
    }

    public getAllReleaseNotes() {
        return this.api.get(`/ReleaseNotes`);
    }

    public getReleaseNote(fileId: string) {
        return this.api.getFileFetchWithHeaders(`/ReleaseNotes/${fileId}`);
    }
    
    public postReleaseNote(file: FormData) {
        return this.api.file(`/ReleaseNotes`, file);
    }

    public deleteReleaseNote(fileId: string) {
        return this.api.delete(`/ReleaseNotes/${fileId}`);
    }

    public async getPreferences() {
        return await this.api.get('/account/preferences');
    }
}

export class UserStore {
    private userApi: UserAPI;
    private RootStore: RootStore;
    @observable public user: IUser;
    @observable public permissions: string[];
    @observable public releaseNotes: IReleaseNotes[] = [];

    constructor(rootStore: RootStore) {
        this.RootStore = rootStore;
        this.userApi = new UserAPI(this.RootStore.Api);
    }

    public getAllReleaseNotes = async () => {
        const data: IReleaseNotes[] = await this.userApi.getAllReleaseNotes();
        runInAction(() => {
            this.releaseNotes = data;
        });
        return data;
    }
    
    public async getReleaseNote(fileId: string, filename: string) {
        return this.userApi.getReleaseNote(fileId)
            .then(response => {
                if (response.ok) {
                    return response;
                } else {
                    throw new Error('Failed to download file');
                }
            })
            .then(res => res.blob())
            .then(blob => {
                const file = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = file;
                link.setAttribute('download', filename);  // 3. Append to html page
                document.body.appendChild(link);  // 4. Force download
                link.click();  // 5. Clean up and remove the link
                if (link.parentNode) {
                    link.parentNode.removeChild(link);
                }
            })
            .catch(error => {
                this.RootStore.MessageStore.addMessage(error, 'danger');
            });

    }

    public async openReleaseNoteInNewTab(fileId: string) {
        console.log(fileId);
        return this.userApi.getReleaseNote(fileId)
            .then(response => {
                if (response.ok) {
                    return response;
                } else {
                    throw new Error('Failed to download file');
                }
            })
            .then(res => res.blob())
            .then(blob => {
                const file = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = file;
                link.setAttribute('target', '_blank');  // 3. Append to html page
                document.body.appendChild(link);  // 4. Force download
                link.click();  // 5. Clean up and remove the link
                if (link.parentNode) {
                    link.parentNode.removeChild(link);
                }
            })
            .catch(error => {
                this.RootStore.MessageStore.addMessage(error, 'danger');
            });

    }

    public postReleaseNote(file: any) {
        return this.userApi.postReleaseNote(file);
    }

    public deleteReleaseNote(fileId: string) {
        return this.userApi.deleteReleaseNote(fileId);
    }

    public async getProfile() {
        const token = auth.getToken();
        if (!token) {
            return;
        }

        // const expired = auth.isTokenExpired(token);
        // if (expired) {
        //     return;
        // }

        const profile: any = await this.userApi.getProfile();
        this.getPermissions();
        runInAction(() => {
            this.user = profile;
        });
        return profile;
    }

    public getPermissions = () => {
        const jwtDecoded = auth.decodeToken();
        if (jwtDecoded.permission) {
            if(Array.isArray(jwtDecoded.permission)) {
                runInAction(() => {
                    this.permissions = jwtDecoded.permission;
                });
            } else {
                runInAction(() => {
                    this.permissions = [jwtDecoded.permission];
                });
            }
        }
    }

    public async putProfile(profile: IUser) {
        const jsProfile = toJS(profile);

        if (jsProfile.password === '') {
            delete jsProfile.password;
            delete jsProfile.confirmPassword;
        }

        const savedProfile: IUser = await this.userApi.putProfile(jsProfile);

        runInAction(() => {
            this.user = savedProfile;
        });
        return savedProfile;
    }

    @action
    public updateUser(userObject: IUser) {
        this.user = userObject;
    }

    @action
    public clearUser() {
        this.user = undefined as unknown as IUser;
    }
    
    public getUser() {
        return toJS(this.user);
    }

    public hasRoles(roles: string | string[]) {
        let hasRole: boolean = false;
        if (!this.user) {
            return false;
        }
        const currentRoles = this.user.roles;

        if (roles instanceof Array) {
            roles.map((role: string) => {
                if (currentRoles && currentRoles.indexOf(role) > -1) {
                    hasRole = true;
                }
            });

        } else if (typeof roles === 'string') {
            if (currentRoles && currentRoles.indexOf(roles) > -1) {
                hasRole = true;
            }
        }

        return hasRole;
    }

    public hasPermission(permission: string) {
        if (!this.user) {
            return false;
        }
        if (!this.permissions) {
            return false;
        }
        return this.permissions.some(perm => perm === permission);
    }

    public hasGenericPermission(permission: string) {
        if (!this.user) {
            return false;
        }
        if (!this.permissions) {
            return false;
        }
        return this.permissions.some(perm => perm === permission || getGenericPermission(perm) === permission);
    }

    public getPreference(key: string) {
        if (this.user.preferences) {
            return this.user.preferences[key];
        }
        return undefined;
    }

    public userProfile() {
        const decoded: any = auth.decodeToken();

        return decoded;
    }

    public isAdmin() {
        let isAdmin: boolean = false;
        if (this.user && this.user.roles) {
            isAdmin = (this.user.roles.length > 0 && this.user.roles.indexOf('Admin') > -1);
        }

        return isAdmin;
    }

    public isFieldSupervisor() {
        let isFieldSupervisor: boolean = false;
        if (this.user && this.user.roles) {
            isFieldSupervisor = (this.user.roles.length > 0 && this.user.roles.indexOf('FieldSupervisor') > -1);
        }

        return isFieldSupervisor;
    }

    public isNotMudEngineer() {
        if (this.user && this.user.roles) {
            return this.user.roles.length > 0 && this.user.roles.indexOf('MudEngineer') < 0;
        }
        return false;
    }

    public resetPassphrase() {
        this.userApi.resetPassphrase()
            .then(data => {
                if (data) {
                    this.RootStore.MessageStore.addMessage('Passphrase reset email sent.  Previous passphrase is now invalid.', 'success');
                }
            });
    }
}

const getGenericPermission = (permission: string) => {
    const lastDot = permission.lastIndexOf('.');
    if (lastDot > 0) {
        return permission.slice(0, lastDot);
    }
    return permission;
};


// WEBPACK FOOTER //
// ./src/Stores/User/UserStore.tsx